/**
 * MDM action layout — purely declarative map from catalog id → where the
 * button renders and in what order. Spec §5.4.
 *
 * Duplicated from `apps/web/src/features/mdm-devices/components/actions/` per
 * the Phase 7 / spec §6.2 rule-of-three convention (ESLint boundary rule
 * blocks cross-app imports). Keep the two files in sync; when Phase 2 adds a
 * third consumer, extract to a shared package.
 *
 * `slot: 'toolbar'` = rendered inline as an `MdmActionButton`.
 * `slot: 'overflow'` = rendered inside the `MdmActionDropdown` (… menu).
 * `order` = ascending display order within the slot.
 * `variant` = optional override for button styling (destructive shown red).
 *
 * Missing-id fallback (forward compat — server ships a new command id before
 * the client knows about it) is applied by the consumer as
 *   `{ slot: 'overflow', order: 999 }`.
 */
import type { MdmCommandId } from '@staff-app/shared-types';

export type MdmActionSlot = 'toolbar' | 'overflow';

export interface MdmActionLayoutEntry {
  slot: MdmActionSlot;
  order: number;
  variant?: 'default' | 'secondary' | 'destructive';
}

export const MDM_ACTION_LAYOUT: Record<MdmCommandId, MdmActionLayoutEntry> = {
  IDENTIFY_SCREEN: { slot: 'toolbar', order: 10 },
  MDM_REBOOT: { slot: 'toolbar', order: 20, variant: 'destructive' },
  MDM_REFRESH: { slot: 'toolbar', order: 30 },
  MDM_CAPTURE_SCREENSHOT: { slot: 'toolbar', order: 40 },
  MDM_TOGGLE_REMOTE_CONTROL: { slot: 'toolbar', order: 50 },
  MDM_OPEN_REMOTE_VIEWER: { slot: 'toolbar', order: 60 },
  MDM_HDMI_CEC: { slot: 'overflow', order: 100 },
  MDM_RS232: { slot: 'overflow', order: 110 },
  MDM_GET_USED_STORAGE: { slot: 'overflow', order: 120 },
  MDM_INSTALL_OPTISIGNS_VERSION: { slot: 'overflow', order: 130 },
  // Auto-update pair collapses visually via the device-state gate — only one
  // of the pair is ever resolved into `availableCommands` at a time.
  MDM_DISABLE_AUTOUPDATE: { slot: 'overflow', order: 140 },
  MDM_ENABLE_AUTOUPDATE: { slot: 'overflow', order: 140 },
  MDM_INSTALL_APK: { slot: 'overflow', order: 150 },
  MDM_OTA_UPDATE: { slot: 'overflow', order: 160, variant: 'destructive' },
  MDM_SET_TIMEZONE: { slot: 'overflow', order: 200 },
  MDM_WIFI_MANAGEMENT: { slot: 'overflow', order: 210 },
  MDM_VIEW_DEVICE_INFO: { slot: 'overflow', order: 220 },
  MDM_COMMAND_HISTORY: { slot: 'overflow', order: 230 },
  // Platform-api (Phase A — Zoom Rooms)
  MDM_SYNC_NOW: { slot: 'toolbar', order: 25 },
  MDM_VIEW_ISSUES: { slot: 'overflow', order: 240 },
  MDM_OPEN_EXTERNAL_ADMIN: { slot: 'overflow', order: 250 },
  // Advanced / power-user; rendered last and un-tinted so it doesn't visually
  // compete with OTA Update as the dropdown's destructive anchor.
  MDM_EXECUTE_REMOTE_COMMAND: { slot: 'overflow', order: 900 },
};

/**
 * Look up the layout entry for a command id. Falls back to a safe overflow
 * bucket with order=999 so new catalog entries never crash older clients.
 */
export function getLayoutEntry(
  id: MdmCommandId,
  layout: Record<MdmCommandId, MdmActionLayoutEntry> = MDM_ACTION_LAYOUT,
): MdmActionLayoutEntry {
  return layout[id] ?? { slot: 'overflow', order: 999 };
}
